import type { MouseEvent } from "react";
import { useState } from "react";
import {
  Box,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment-timezone";
import { CreditCalculationStatus, CreditSource } from "@vmosolutions/oc-interfaces";
import type { ICreditDisplay, IPerDiem } from "@vmosolutions/oc-interfaces";

type ICreditValueCellProps = {
  /** One projected calculated credit row - see buildCreditDisplay. */
  row: ICreditDisplay;
};

/** Trailing zeros trimmed: 24.50 -> "24.5", 24.00 -> "24". */
export const formatCredit = (credit: number): string =>
  Number.isFinite(credit) ? `${Number(credit.toFixed(2))}` : "-";

const formatPerDiem = (perDiem: IPerDiem | undefined): string =>
  perDiem ? `${Number(perDiem.amount.toFixed(2))} ${perDiem.currency}` : "";

/** Calculation timestamps are stamped in UTC - shown as such so they can't be misread as local. */
const formatCalculatedAt = (calculatedAt: number | Date | undefined): string =>
  calculatedAt ? `${moment.utc(calculatedAt).format("MMM-DD-YYYY HH:mm")}Z` : "Unknown";

const sourceDescription = (source: CreditSource): string =>
  source === CreditSource.current
    ? "The latest calculation from the pairing's content. Recalculated after each save - unsaved edits are not included."
    : source === CreditSource.planned
      ? "The official planned calculated credit. Written only by pairing build and the admin Recalculate action."
      : "";

/** A non-ok record still shows its last known good values, so say why they may be out of date. */
const statusNote = (row: ICreditDisplay): string | undefined => {
  switch (row.status) {
    case CreditCalculationStatus.error:
      return `Last calculation failed${row.error?.at ? ` ${formatCalculatedAt(row.error.at)}` : ""}${
        row.error?.message ? `: ${row.error.message}` : "."
      } The values below are the last known good ones.`;
    case CreditCalculationStatus.unsupported:
      return "Credit calculation is not configured for this crew grouping.";
    default:
      return undefined;
  }
};

/**
 * One calculated credit value - total only, read-only, with the per-day breakdown and the calculation
 * timestamp behind a click. Used as a cell in CreditSummaryCard's Planned/Current columns.
 *
 * Calculated credit is never editable - an edit would be silently overwritten by the next calculation -
 * so this renders a label rather than an input, matching getCreditReadOnlyReason.
 */
export const CreditValueCell = (props: ICreditValueCellProps): JSX.Element => {
  const { row } = props;
  const theme: Theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const open = Boolean(anchorEl);

  const note = statusNote(row);
  // Not usable means the number may be shown but must not be acted on - flag it rather than hide it.
  // `usable` already folds status in (false only for a non-ok calculated row), so an operator-entered row,
  // which has no status at all, is never flagged.
  const flagged = !row.usable;
  const perDay = row.creditPerDay ?? [];
  const hasPerDiem = row.perDiem !== undefined || perDay.some((day) => day.perDiem !== undefined);
  const hasReason = perDay.some((day) => day.reason !== undefined && day.reason !== "");

  const handleClick = (event: MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  return (
    <>
      <Tooltip title="Read only - click for the daily breakdown">
        <Box
          className="calculated_credit_value"
          onClick={handleClick}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "2px 5px",
            fontSize: "12px",
            cursor: "pointer",
            // Read-only, so it deliberately does not use the input background the editable fields have.
            border: `1px dashed ${flagged ? theme.custom.forms.warningColor : theme.custom.forms.inputBorder}`,
            color: `${flagged ? theme.custom.forms.warningColor : theme.custom.forms.inputText} !important`,
          }}
        >
          {formatCredit(row.credit)}
          {flagged ? (
            <ErrorOutlineIcon sx={{ fontSize: 13 }} />
          ) : (
            <InfoOutlinedIcon sx={{ fontSize: 13, opacity: 0.7 }} />
          )}
        </Box>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ padding: "10px 12px", minWidth: 280, maxWidth: 420 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{`${row.displayName}`}</Typography>
          <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary, mb: 0.5 }}>
            {sourceDescription(row.source)}
          </Typography>
          <Typography sx={{ fontSize: 11 }}>
            {`Total: ${formatCredit(row.credit)}${row.perDiem ? ` | Per Diem: ${formatPerDiem(row.perDiem)}` : ""}`}
          </Typography>
          <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
            {`Calculated: ${formatCalculatedAt(row.calculatedAt)}`}
          </Typography>
          {note && (
            <Typography sx={{ fontSize: 11, mt: 0.5, color: theme.custom.forms.warningColor }}>{note}</Typography>
          )}
          {perDay.length > 0 ? (
            <TableContainer sx={{ maxHeight: 220, overflow: "auto", mt: 0.5 }}>
              <Table
                size="small"
                stickyHeader
                sx={{
                  "& td, & th": { padding: "2px 8px", fontSize: 11, border: "none" },
                  "& thead th": { backgroundColor: theme.palette.background.paper },
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* The day keys are bucketed in dayTimezoneId, not the viewer's zone - name it in the header. */}
                    <TableCell>{`Day${row.dayTimezoneId ? ` (${row.dayTimezoneId})` : ""}`}</TableCell>
                    <TableCell align="right">Credit</TableCell>
                    {hasPerDiem && <TableCell align="right">Per Diem</TableCell>}
                    {hasReason && <TableCell>Reason</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {perDay.map((day) => (
                    <TableRow key={day.date}>
                      <TableCell>{day.date}</TableCell>
                      <TableCell align="right">{formatCredit(day.credit)}</TableCell>
                      {hasPerDiem && <TableCell align="right">{formatPerDiem(day.perDiem)}</TableCell>}
                      {hasReason && <TableCell>{day.reason ?? ""}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ fontSize: 11, mt: 0.5, color: theme.palette.text.secondary }}>
              No daily breakdown was provided for this calculation.
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};
